import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Edit, FileText, Upload, Send, CheckCircle2, RefreshCw,
  AlertCircle, ChevronDown, ChevronRight
} from 'lucide-react';
import { Package } from 'lucide-react';
import { BRAND_COLORS, BRAND_LABELS, API, BACKEND_URL } from '@/constants/bluemonk';

export const AdminDeliverablesSection = ({ items, headers, actor, onRefresh, onEdit, onAdd }) => {
  const [expandedItem, setExpandedItem] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [respondingTo, setRespondingTo] = useState(null);

  const handleSubmitForReview = async (id) => {
    try {
      await axios.post(`${API}/deliverables/${id}/submit-for-review`, {}, { headers });
      toast.success('Submitted for client review');
      onRefresh();
    } catch (error) {
      toast.error('Failed to submit');
    }
  };

  const handleFileUpload = async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/deliverables/${id}/upload-file`, formData, { headers });
      toast.success('File uploaded');
      onRefresh();
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handleAddressRevision = async (itemId, revisionId, resubmit = false) => {
    if (!responseText.trim()) {
      toast.error('Please provide a response');
      return;
    }
    try {
      await axios.post(`${API}/deliverables/${itemId}/address-revision/${revisionId}`, {
        response: responseText,
        resubmit
      }, { headers });
      toast.success(resubmit ? 'Revision addressed and resubmitted' : 'Revision addressed');
      setResponseText('');
      setRespondingTo(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to address revision');
    }
  };

  const handleAddComment = async (itemId, content) => {
    try {
      await axios.post(`${API}/deliverables/${itemId}/comments`, {
        actor,
        content,
        authorType: 'admin'
      }, { headers });
      toast.success('Comment added');
      onRefresh();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const inProgress = items.filter(i => i.status === 'in-progress');
  const awaitingReview = items.filter(i => ['submitted-for-review', 'revision-submitted'].includes(i.status));
  const revisionRequested = items.filter(i => i.status === 'revision-requested');
  const approved = items.filter(i => i.status === 'approved');

  const renderDeliverable = (item) => {
    const colors = BRAND_COLORS[item.brand];
    const isExpanded = expandedItem === item.id;
    const openRevisions = item.revisionRequests?.filter(r => r.status === 'open') || [];
    
    return (
      <Card key={item.id} className={`border-l-4 ${
        item.status === 'revision-requested' ? 'border-l-amber-500 bg-amber-50/30' :
        item.status === 'approved' ? 'border-l-green-500 bg-green-50/30' :
        ['submitted-for-review', 'revision-submitted'].includes(item.status) ? 'border-l-blue-500' :
        'border-l-slate-300'
      }`}>
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold">{item.title}</h3>
                <Badge className={`${colors.badge} text-white text-xs`}>
                  {BRAND_LABELS[item.brand]}
                </Badge>
                <Badge variant="outline" className={`capitalize ${
                  item.status === 'revision-requested' ? 'border-amber-500 text-amber-700' :
                  item.status === 'approved' ? 'border-green-500 text-green-700' :
                  ['submitted-for-review', 'revision-submitted'].includes(item.status) ? 'border-blue-500 text-blue-700' :
                  ''
                }`}>
                  {item.status.replace(/-/g, ' ')}
                </Badge>
              </div>
              {item.description && (
                <p className="text-sm text-slate-500 mb-2">{item.description}</p>
              )}
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span><span className="font-medium">Files:</span> {item.files?.length || 0}</span>
                {item.submittedAt && (
                  <span><span className="font-medium">Submitted:</span> {new Date(item.submittedAt).toLocaleDateString()}</span>
                )}
                {item.reviewDeadline && (
                  <span><span className="font-medium">Review by:</span> {item.reviewDeadline}</span>
                )}
                {item.approvalRecord && (
                  <span className="text-green-600">
                    <CheckCircle2 className="h-4 w-4 inline mr-1" />
                    Approved by {item.approvalRecord.approvedBy?.name}
                  </span>
                )}
              </div>
              
              {item.files?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.files.map((file) => (
                    <a
                      key={file.id}
                      href={`${BACKEND_URL}${file.fileUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs hover:bg-slate-200"
                    >
                      <FileText className="h-3 w-3" />
                      {file.fileName}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 ml-4">
              {item.status !== 'approved' && (
                <label className="cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleFileUpload(item.id, e.target.files[0]);
                      }
                    }}
                  />
                  <Button size="sm" variant="outline" asChild>
                    <span>
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </span>
                  </Button>
                </label>
              )}
              {item.status === 'in-progress' && item.files?.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => handleSubmitForReview(item.id)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Submit for Review
                </Button>
              )}
              {item.status === 'revision-requested' && openRevisions.length === 0 && item.files?.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => handleSubmitForReview(item.id)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Resubmit
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onEdit(item)}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setExpandedItem(isExpanded ? null : item.id)}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="px-4 pb-4 border-t bg-slate-50/50">
            {openRevisions.length > 0 && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-semibold text-amber-800 mb-3">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  Open Revision Requests ({openRevisions.length})
                </p>
                {openRevisions.map((rev) => (
                  <div key={rev.id} className="mb-4 last:mb-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-amber-700">
                          <span className="font-medium">{rev.requestedBy?.name}</span>
                          <span className="text-amber-500 text-xs ml-2">
                            {new Date(rev.requestedAt).toLocaleDateString()}
                          </span>
                        </p>
                        <p className="text-sm text-slate-700 mt-1">"{rev.reason}"</p>
                      </div>
                    </div>
                    {respondingTo === rev.id ? (
                      <div className="mt-3 space-y-2">
                        <Textarea
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          placeholder="Your response..."
                          rows={2}
                          className="text-sm"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAddressRevision(item.id, rev.id, false)}
                          >
                            Mark Addressed
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setRespondingTo(null); setResponseText(''); }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => setRespondingTo(rev.id)}
                      >
                        Respond
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {item.comments?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Comments</p>
                <div className="space-y-2">
                  {item.comments.map((comment) => (
                    <div key={comment.id} className="text-sm p-2 bg-white rounded border">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{comment.author?.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {comment.authorType === 'admin' ? 'CV' : 'Client'}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {new Date(comment.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-slate-600 mt-1">{comment.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {item.approvalRecord && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <CheckCircle2 className="h-4 w-4 inline mr-1" />
                  Approved by <span className="font-medium">{item.approvalRecord.approvedBy?.name}</span>
                  {' '}on {new Date(item.approvalRecord.approvedAt).toLocaleString()}
                  {item.approvalRecord.note && (
                    <span className="block mt-1 text-green-700">Note: {item.approvalRecord.note}</span>
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Deliverables</h1>
        <Button onClick={onAdd} className="bg-primary hover:bg-primary/90" data-testid="add-deliverable-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Deliverable
        </Button>
      </div>

      {revisionRequested.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-amber-700 mb-3 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Needs Attention ({revisionRequested.length})
          </h2>
          <div className="space-y-3">
            {revisionRequested.map(renderDeliverable)}
          </div>
        </div>
      )}

      {awaitingReview.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-blue-700 mb-3">
            Awaiting Client Review ({awaitingReview.length})
          </h2>
          <div className="space-y-3">
            {awaitingReview.map(renderDeliverable)}
          </div>
        </div>
      )}

      {inProgress.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-700 mb-3">
            In Progress ({inProgress.length})
          </h2>
          <div className="space-y-3">
            {inProgress.map(renderDeliverable)}
          </div>
        </div>
      )}

      {approved.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-green-700 mb-3">
            Approved ({approved.length})
          </h2>
          <div className="space-y-3">
            {approved.map(renderDeliverable)}
          </div>
        </div>
      )}
      
      {items.length === 0 && (
        <Card className="p-8 text-center">
          <Package className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-slate-700">No deliverables yet</p>
          <p className="text-slate-500">Create your first deliverable to get started</p>
        </Card>
      )}
    </div>
  );
};
